import { useState, useEffect } from 'react';
import { PoiDialogInput } from './PoiDialogInput';
import { PoiDialogButton } from './PoiDialogButton';
import { APIMessage } from '@components/ui/messages/APIMessage';
import { AddByIntersectionPointTooltip } from '@components/ui/tooltips/PoiTooltips';
/**
 * Modal component for adding custom intersection point coordinates.
 * Allows user to input latitude and longitude for a custom intersection point,
 * with validation feedback displayed inline.
 * 
 * @param {Object} props
 * @param {number} props.currentLat - Current intersection point latitude for form pre-fill
 * @param {number} props.currentLng - Current intersection point longitude for form pre-fill
 * @param {Function} props.onSubmit - Callback when form is submitted: (lat, lng) => void
 * @param {Function} props.onCancel - Callback when modal is cancelled
 * @param {string} props.validationError - Error message to display (e.g., distance validation error)
 * 
 * @returns {JSX.Element}
 * 
 * @category Components - Map - POI Dialog
 */
export const CustomIntersectModal = ({ 
  currentLat, 
  currentLng, 
  onSubmit, 
  onCancel,
  validationError = '',
  isEdit = true
}) => {
  const [lat, setLat] = useState(currentLat ? Number(currentLat).toFixed(5) : '');
  const [lng, setLng] = useState(currentLng ? Number(currentLng).toFixed(5) : '');
  const [error, setError] = useState(validationError);

  // Update error state when validationError prop changes
  useEffect(() => {
    setError(validationError);
  }, [validationError]);

  const handleSubmit = async() => {
    // Basic validation - ensure values are numbers
    if (!lat || !lng) {
      setError('Both latitude and longitude are required');
      return;
    }

    const latNum = parseFloat(lat);
    const lngNum = parseFloat(lng);

    if (isNaN(latNum) || isNaN(lngNum)) {
      setError('Latitude and longitude must be valid numbers');
      return;
    }

    const result = await onSubmit(latNum, lngNum);

    if(result.success){
      setLat(currentLat ? Number(currentLat).toFixed(5) : '');
      setLng(currentLng ? Number(currentLng).toFixed(5) : '');
    }
  };

  const handleCancel = () => {
    // Reset form on cancel
    setLat(currentLat ? Number(currentLat).toFixed(5) : '');
    setLng(currentLng ? Number(currentLng).toFixed(5) : '');
    setError('');
    onCancel();
  };

  const handleFixDecimals = (setter) => {
    setter(prev => {
      if (!prev) return '';
      const num = Number(prev);
      return isNaN(num) ? prev : num.toFixed(5);
    });
  };


  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg p-6 w-96 border-2 border-gray-300">

        {/* show Add or Edit title depending on where the modal was opened from - 'Add Intersection Point' button, or 'Edit lat/lng' button in PoiDialog */}
        {/* show different tooltips as well */}
        <h2 className="text-md font-bold mb-4">
          {isEdit ? 'Edit Intersection Point' : 'Add POI Intersection Point'}
          {!isEdit ? <AddByIntersectionPointTooltip /> : null} 
        </h2>

        <div className="space-y-3 mb-4">
          <PoiDialogInput
            label="Lat"
            value={lat}
            onChange={(e) => {
              setLat(e.target.value);
              setError(''); // Clear error when user starts editing
            }}
            onBlur={() => handleFixDecimals(setLat)}
            disabled={false}
            boldLabel={false}
            placeholder="e.g., 40.7128"
          />

          <PoiDialogInput
            label="Long"
            value={lng}
            onChange={(e) => {
              setLng(e.target.value);
              setError(''); // Clear error when user starts editing
            }}
            onBlur={() => handleFixDecimals(setLng)}
            disabled={false}
            boldLabel={false}
            placeholder="e.g., -74.0060"
          />

          {/* Error message display */}
          <APIMessage message={error} isError={error}/>
        </div>

        {/* Buttons */}
        <div className="flex justify-end gap-3 mt-6">
            <PoiDialogButton
                text="Submit"
                variant="success"
                onClick={handleSubmit}
            />
          <PoiDialogButton
            text="Cancel"
            variant="cancel"
            onClick={handleCancel}
          />

        </div>
      </div>
    </div>
  );
};
